import React, { useLayoutEffect, useMemo, useRef } from 'react';

import cn from 'classnames';

import { ICommentGroup } from '@entities/message/model/types';
import { CircularLoader, Flex } from '@shared/ui';
import {
    DateChip,
    EmptyCommentsPlaceholder,
    UnreadDivider,
    MessagesList,
    MessageItem,
} from '@widgets/Comments/ui';

import cls from './CommentsMain.module.css';

interface ICommentsMainProps {
    messagesData: ICommentGroup[];
    className?: string;
    isInitialLoading?: boolean;
}
export const CommentsMain = ({
    className,
    messagesData = [],
    isInitialLoading = false,
}: ICommentsMainProps) => {
    const messagesListRef = useRef<HTMLUListElement | null>(null);
    const messagesCount = useMemo(
        () => messagesData.reduce((count, group) => count + (group.comments?.length || 0), 0),
        [messagesData]
    );
    const firstUnreadMessageId = useMemo(
        () =>
            messagesData
                .flatMap((group) => group.comments || [])
                .find((message) => message.isUnread)?.id || null,
        [messagesData]
    );

    useLayoutEffect(() => {
        if (!messagesListRef.current) {
            return;
        }

        messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }, [messagesCount]);

    const renderContent = () => {
        if (isInitialLoading) {
            return (
                <div className={cls.LoaderContainer}>
                    <CircularLoader size={32} thickness={3} />
                </div>
            );
        }

        if (messagesCount === 0) {
            return <EmptyCommentsPlaceholder />;
        }

        return (
            <MessagesList ref={messagesListRef}>
                {messagesData.map((group) => (
                    <React.Fragment key={group.datetime}>
                        <DateChip datetime={group.datetime} />
                        {group?.comments?.map((message) => (
                            <React.Fragment key={message.id}>
                                {firstUnreadMessageId === message.id && <UnreadDivider />}
                                <MessageItem messageData={message} />
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </MessagesList>
        );
    };

    return (
        <Flex as="main" className={cn(cls.CommentsMain, className)}>
            {renderContent()}
        </Flex>
    );
};
